/**
 * Carbon icon button: a square hit target (48px by default) in the secondary
 * icon tone, turning layer-hover with primary ink on hover. `label` is the
 * accessible name; the icon itself is hidden from assistive tech.
 */
import { forwardRef, type ButtonHTMLAttributes, type ReactNode } from 'react'
import clsx from 'clsx'

export interface IconButtonProps extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'children' | 'aria-label'> {
  /** Accessible name ("Export drifts", "Next page"). */
  label: string
  /** The 16px icon. */
  icon: ReactNode
  size?: 48 | 40 | 32
}

const SIZE: Record<NonNullable<IconButtonProps['size']>, string> = {
  48: 'h-12 w-12',
  40: 'h-10 w-10',
  32: 'h-8 w-8',
}

export const IconButton = forwardRef<HTMLButtonElement, IconButtonProps>(function IconButton(
  { label, icon, size = 48, className, type = 'button', ...rest },
  ref,
) {
  return (
    <button
      ref={ref}
      type={type}
      aria-label={label}
      className={clsx(
        'inline-grid flex-none place-items-center border-0 bg-transparent text-[color:var(--icon-secondary)]',
        'transition-colors duration-110 ease-carbon hover:bg-layer-hover hover:text-text-primary',
        'disabled:cursor-not-allowed disabled:bg-transparent disabled:text-[color:var(--text-placeholder)]',
        SIZE[size],
        className,
      )}
      {...rest}
    >
      <span aria-hidden="true" className="grid place-items-center [&>svg]:h-4 [&>svg]:w-4">
        {icon}
      </span>
    </button>
  )
})

export default IconButton
